import type {
  ActivityMemberV2,
  UnionContestTaskConfigItem,
} from '../NewYearUnionGame/types'

export type {
  ActivityMemberV2,
  RdsRankInfoV2,
  UnionContestRewardDetail,
  UnionContestRewardItem,
} from '../NewYearUnionGame/types'

/** 加成卡状态：0 未使用，1 已使用，2 使用中 */
export enum MiddleContestCardStatus {
  unused = 0,
  used = 1,
  using = 2,
}

export interface MiddleContestCardItem {
  card_id: number
  card_odds: number
  card_icon: string
  card_hours: number
  /** 生效剩余时间（秒），使用中卡片展示 */
  expire_ts: number
  /** 过期时间，未使用卡片展示 */
  expire_time: string
  ustatus: MiddleContestCardStatus
  /** 已使用卡片的适用时间 */
  use_time: string
}

export interface MiddleContestCardInfoResponse {
  is_buff_active: boolean
  /** 0 海选，1 决赛 */
  competition_phase: number
  final_contest_start_time: string
  current_ratio: number
  /** 当前 buff 到期剩余时间（秒），无 buff 为 0 */
  buff_expire_ts: number
  /** 0 非主播，1 普通主播，2 会长/管理员 */
  user_identity: number
  /** 是否可以提醒公会长使用加成卡 */
  is_remind_can_use: boolean
  card_list: MiddleContestCardItem[]
  new_offset: number
  more: boolean
  err_msg?: string
  /** 剩余可使用加成卡数量 */
  count: number
}

export interface MiddleContestCardRequestParams {
  offset?: number
  limit?: number
  /** 分页加载时追加 card_list */
  append?: boolean
}

export type MiddleContestCardState = MiddleContestCardInfoResponse

export interface MiddleContestCardStoreState {
  loading: boolean
  error: string | null
  data: MiddleContestCardState
}

/** 号召人员列表来源 tab */
export enum MiddleContestPersonListFrom {
  anchorCallNotComplete = 'anchor_call_not_complete',
  anchorCallCompleted = 'anchor_call_completed',
  myFans = 'my_fans',
  myAttention = 'my_attention',
}

export interface MiddleContestPersonItem {
  mid: number
  avatar: string
  name: string
  /** 完成任务场景才有收益 */
  income?: number
  /** 完成任务后才有加成卡 icon */
  card_icon?: string
  /** 完成任务后才有加成倍数 */
  card_mul?: number
  /** my_fans / my_attention / search 场景：是否已发送助力邀请 */
  is_invite_sent?: boolean
}

/** person_list · data 层公共字段（两种 from 均返回，与 list 同级） */
export interface MiddleContestPersonListPanelMeta {
  /**
   * 号召规则收入门槛（钻石），对应 i18n「主播号召.规则」插值 {0}
   * 文案：每 1 位主播收入超过该值，公会获得 1 次随机抽奖机会
   */
  income_threshold?: number
  /**
   * 一键号召按钮是否可点
   * false：今日已达号召上限等，按钮置灰 disabled
   */
  can_one_click_call?: boolean
  /** 随机加成卡倍数下限（规则文案 1.4–1.5，仅展示，当前仍走 i18n 写死） */
  card_mul_min?: number
  /** 随机加成卡倍数上限 */
  card_mul_max?: number
}

export interface MiddleContestPersonListResponse
  extends MiddleContestPersonListPanelMeta {
  err_msg?: string
  list: MiddleContestPersonItem[]
}

export interface MiddleContestPersonListRequestParams {
  from: MiddleContestPersonListFrom
}

export interface MiddleContestPersonListState
  extends MiddleContestPersonListPanelMeta {
  from: MiddleContestPersonListFrom | ''
  err_msg: string
  income_threshold: number
  can_one_click_call: boolean
  card_mul_min: number
  card_mul_max: number
  list: MiddleContestPersonItem[]
}

export interface MiddleContestPersonListStoreState {
  loading: boolean
  error: string | null
  data: MiddleContestPersonListState
}

/** 子榜来源：海选 / 晋级 / 海选直通车 */
export enum MiddleContestAuditionToRankFrom {
  audition = 'audition',
  promotion = 'promotion',
  auditionToNext = 'audition_to_next',
  /** 仅父榜 rank · 全球巅峰赛 / 全民助力 */
  global = 'global',
}

export interface MiddleContestSupportAnchor {
  member: ActivityMemberV2
  rank_no: number
  score: number
}

/** POST /live/activity/get_2026_year_middle_contest_to_rank */
export interface MiddleContestAuditionToRankResponse {
  err_msg?: string
  offset?: number
  more?: boolean
  /** 当前用户在本公会子榜中的排名 */
  own?: MiddleContestSupportAnchor
  support_anchors?: MiddleContestSupportAnchor[]
}

export interface MiddleContestAuditionToRankRequestParams {
  union_id: number
  from: MiddleContestAuditionToRankFrom
  date: string
  offset?: number
  /** 分页加载时追加 support_anchors */
  append?: boolean
}

export interface MiddleContestAuditionToRankState {
  union_id: number
  from: MiddleContestAuditionToRankFrom | ''
  date: string
  err_msg: string
  offset: number
  more: boolean
  own: MiddleContestSupportAnchor | null
  support_anchors: MiddleContestSupportAnchor[]
}

export interface MiddleContestAuditionToRankStoreState {
  loading: boolean
  error: string | null
  data: MiddleContestAuditionToRankState
}

/** 父榜来源（与 MiddleContestAuditionToRankFrom 一致） */
export type MiddleContestRankFrom = MiddleContestAuditionToRankFrom

export interface MiddleContestRankUnion {
  id: number
  name: string
  icon: string
  area_code?: number
  /** from=global · 公会所属国家国旗 URL（榜单行 / Top3 卡位展示） */
  flag_icon?: string
}

export interface MiddleContestRankReward {
  id?: number
  name: string
  icon: string
  show_type?: number
  num?: number
  privilege_id?: number
  privilege_type?: number
  reward_config_id?: number
  /** 奖励去重标识 */
  tag?: string
}

export interface MiddleContestRankListItem {
  union: MiddleContestRankUnion
  is_reach_next?: boolean
  rank_no: number
  score: number
  /** 净胜下一名的分数；>0 时展示领先条 */
  gap_with_prev: number
  /** 目标分数；展示在 targetLabel / 目标面板 */
  reward_need_score: number
  /** 加成卡生效 icon；有值时展示 boosterEffectIcon */
  speed_card_icon?: string
  union_leader_rewards: MiddleContestRankReward[]
  anchor_rewards: MiddleContestRankReward[][]
}

export interface MiddleContestRankOwn {
  union: MiddleContestRankUnion
  rank_no: number
  score: number
  gap_with_prev: number
  /** 直通/目标门槛分（父榜 own 与 list 均可能下发） */
  reward_need_score?: number
  is_reach_next?: boolean
  speed_card_icon?: string
  union_leader_rewards: MiddleContestRankReward[]
  anchor_rewards: MiddleContestRankReward[][]
  my_support_info?: MiddleContestSupportAnchor
}

/** from=global · 全民助力国家轮播单项 */
export interface MiddleContestGlobalCountryItem {
  /** 国家码（点赞 action 传 country_code） */
  country_code: number
  /** 国旗 URL */
  flag_icon: string
  /** 当前点赞数 */
  like_count: number
  /** 已转化助力积分 */
  assist_points: number
  /** 最新增量；>0 时展示 +N 角标 */
  assist_points_delta?: number
  /** 当前用户是否已点赞 */
  is_liked?: boolean
}

/** from=global · 全民助力国家列表（与 rank 同响应） */
export interface MiddleContestGlobalCountryList {
  /** 默认轮播展示的国家码 */
  default_country_code?: number
  list: MiddleContestGlobalCountryItem[]
}

/** POST /live/activity/get_2026_year_middle_contest_rank */
export interface MiddleContestRankResponse {
  err_msg?: string
  offset?: number
  new_offset?: number
  more?: boolean
  group_list?: [number, number][]
  list?: MiddleContestRankListItem[]
  own?: MiddleContestRankOwn | null
  rule_list?: MiddleContestRankReward[]
  /** 仅 from=global：Points bonus 国家轮播 */
  country_list?: MiddleContestGlobalCountryList | null
  /** from=audition_to_next：直通门槛分（data 根字段） */
  reward_need_score?: number
}

export interface MiddleContestRankRequestParams {
  from: MiddleContestRankFrom
  /** 晋级赛 tab：1-4 四个组 */
  tab?: number
  /** 海选直通车日期 */
  date?: string
  offset?: number
  limit?: number
  /** 分页加载时追加 list */
  append?: boolean
}

export interface MiddleContestRankState {
  from: MiddleContestRankFrom | ''
  tab: number
  date: string
  err_msg: string
  offset: number
  more: boolean
  group_list: [number, number][]
  list: MiddleContestRankListItem[]
  own: MiddleContestRankOwn | null
  rule_list: MiddleContestRankReward[]
  country_list: MiddleContestGlobalCountryList | null
  /** from=audition_to_next：直通门槛分（data 根字段） */
  reward_need_score: number
}

export interface MiddleContestRankStoreState {
  loading: boolean
  error: string | null
  data: MiddleContestRankState
}

/** 2026 年中公会赛工会动作类型 */
export enum MiddleContestUnionActionFrom {
  clickLike = 'click_like',
  /** 主播提醒（含提醒公会长使用未使用加成卡） */
  anchorRemind = 'anchor_remind',
  /** 公会长使用加成卡 */
  useCard = 'use_card',
  leaderRemind = 'leader_remind',
  leaderStartTask = 'leader_start_task',
  /** 搜索用户 mid（全球赛邀请好友） */
  search = 'search',
  /** 全球赛：向好友发送助力点赞邀请私信 */
  inviteLike = 'invite_like',
}

export interface MiddleContestUnionActionResponse {
  err_msg: string
  /** from=search 时返回匹配用户（与 person_list 行字段一致） */
  user?: MiddleContestPersonItem
  /** 兼容旧字段 */
  member?: MiddleContestPersonItem
  list?: MiddleContestPersonItem[]
  like_cnt?: number
  /** from=click_like 时返回最新全民助力国家列表 */
  country_list?: MiddleContestGlobalCountryList | null
}

export interface MiddleContestUnionActionRequestParams {
  from: MiddleContestUnionActionFrom
  /** 点赞时传国家码 */
  country_code?: number
  /** 搜索用户 mid */
  to_mid?: number
  /** from=use_card 时传加成卡 id */
  card_id?: number
}

export interface MiddleContestUnionActionState {
  loading: boolean
  error: string | null
  lastFrom: MiddleContestUnionActionFrom | ''
  lastResult: MiddleContestUnionActionResponse | null
}

/** 主播提醒公会长使用未使用的加成卡 */
export interface MiddleContestRemindUseCardRequestParams {
  card_id: number
}

export interface MiddleContestRemindUseCardResponse {
  err_msg: string
}

export interface MiddleContestRemindUseCardState {
  loading: boolean
  error: string | null
  lastCardId: number
  lastResult: MiddleContestRemindUseCardResponse | null
}

/** 通用抽奖结果状态 */
export enum CommonActivityLotteryStatus {
  success = 0,
  ticketNotEnough = 1,
  timesLimitExceeded = 2,
}

export interface CommonActivityLotteryPrizeItem {
  id: number
  name: string
  icon: string
  value: number
}

/** POST /live/commonactivity/lottery */
export interface MiddleContestUnionLotteryResponse {
  err_msg: string
  ticket_num?: number
  status?: CommonActivityLotteryStatus
  id?: number
  name?: string
  icon?: string
  value?: number
  prize_items?: CommonActivityLotteryPrizeItem[]
  date_time?: string
}

/** @deprecated 使用 MiddleContestUnionLotteryResponse */
export type MiddleContestUnionLotteryWrapResponse =
  MiddleContestUnionLotteryResponse

export interface MiddleContestUnionLotteryRequestParams {
  lottery_id: number
  /** 单次抽奖次数，默认 1 */
  times?: number
}

/** @deprecated 使用 MiddleContestUnionLotteryRequestParams */
export type MiddleContestUnionLotteryWrapRequestParams =
  MiddleContestUnionLotteryRequestParams

export interface MiddleContestLotteryState {
  loading: boolean
  error: string | null
  lastResult: MiddleContestUnionLotteryResponse | null
}

export interface MiddleContestRepechageUnionInfo {
  union_id: number
  union_icon: string
  union_name: string
  group: string
  score: number
}

export interface MiddleContestRepechageRewardItem {
  id: number
  name: string
  view_icon: string
  toast_name: string
  reward_type: number
  tag: string
  quantity: number
  reward_value: number
  has_show_effect: boolean
  privilege_id?: number
  days?: number
}

export interface MiddleContestRepechageSuccessReward {
  income_threshold: number
  /** 抢位/守擂成功即时奖励（预约区展示前 2 项） */
  reward_list: MiddleContestRepechageRewardItem[]
  /** 升级解锁更高奖励上限 · 第一行（取前 4 项） */
  my_group_list?: MiddleContestRepechageRewardItem[]
  /** 升级解锁更高奖励上限 · 第二行（取前 4 项） */
  except_group_list?: MiddleContestRepechageRewardItem[]
}

export interface MiddleContestRepechageResponse {
  err_msg?: string
  is_challenger?: boolean
  is_defender?: boolean
  is_in_repechage?: boolean
  my_union?: MiddleContestRepechageUnionInfo | null
  defender_union?: MiddleContestRepechageUnionInfo | null
  gap_with_prev_group_champion?: number
  /** 抢位赛剩余时间（秒） */
  countdown?: number
  challenger_success_reward?: MiddleContestRepechageSuccessReward | null
  defender_success_reward?: MiddleContestRepechageSuccessReward | null
}

export interface MiddleContestRepechageState {
  err_msg: string
  is_challenger: boolean
  is_defender: boolean
  is_in_repechage: boolean
  my_union: MiddleContestRepechageUnionInfo | null
  defender_union: MiddleContestRepechageUnionInfo | null
  gap_with_prev_group_champion: number
  countdown: number
  challenger_success_reward: MiddleContestRepechageSuccessReward | null
  defender_success_reward: MiddleContestRepechageSuccessReward | null
}

export interface MiddleContestRepechageStoreState {
  loading: boolean
  error: string | null
  data: MiddleContestRepechageState
}

/** 闯关/宝箱记录来源（POST task_record 的 from） */
export enum MiddleContestTaskRecordFrom {
  /** 宝箱记录 */
  box = 'box',
  /** 闯关记录 */
  task = 'task',
}

export interface MiddleContestTaskRecordTaskInfo {
  level: number
  target: number
  income: number
  st: string
  et: string
}

export interface MiddleContestTaskRecordRewardItem {
  icon: string
  reward_time: string
  num: number
  name: string
}

export interface MiddleContestTaskRecordResponse {
  err_msg?: string
  task_list?: MiddleContestTaskRecordTaskInfo[]
  reward_list?: MiddleContestTaskRecordRewardItem[]
}

export interface MiddleContestTaskRecordRequestParams {
  from: MiddleContestTaskRecordFrom | number
}

export interface MiddleContestTaskRecordState {
  from: MiddleContestTaskRecordFrom | number | ''
  err_msg: string
  task_list: MiddleContestTaskRecordTaskInfo[]
  reward_list: MiddleContestTaskRecordRewardItem[]
}

export interface MiddleContestTaskRecordStoreState {
  loading: boolean
  error: string | null
  data: MiddleContestTaskRecordState
}

/** 2026 闯关任务项（在 2025 结构基础上扩展） */
export interface MiddleContestTaskConfigItem
  extends UnionContestTaskConfigItem {
  leader_reward_label?: string
  anchor_reward_labels?: string[]
}

/** 对齐 /live/commonactivity/get_lottery_info prize_list 项 */
export interface MiddleContestLotteryPrizeItem {
  id: number
  name: string
  icon: string
  value: number
  /** ssr：SSR 高亮框 */
  tag?: string
}

export interface MiddleContestLotteryInfo {
  err_msg?: string
  lottery_id?: number
  ticket_num?: number
  /** 1 金币 / 2 宝石 / 3 抽奖积分 */
  ticket_type?: number
  ticket_cost?: number
  lottery_limit?: number
  /** lottery_limit > 0 时才有值 */
  lottery_times?: number
  prize_list?: MiddleContestLotteryPrizeItem[]
}

export interface MiddleContestTaskListResponse {
  group_name?: string
  tasks?: MiddleContestTaskConfigItem[]
  activity_id?: number
  lottery_info?: MiddleContestLotteryInfo[]
  err_msg?: string
}

export interface MiddleContestTaskState {
  /** 数据刷新标志（切换时取反，用于触发子组件更新） */
  done?: boolean
  loading: boolean
  error: string | null
  groupName: string
  list: MiddleContestTaskConfigItem[]
  activity_id: number
  lottery_info: MiddleContestLotteryInfo[]
}

/** 活动首页整体赛程状态 */
export enum MiddleContestHomeStatus {
  /** 预热 */
  preheat = 1,
  /** 进行中 */
  inProgress = 2,
  /** 回顾 */
  review = 3,
}

export interface MiddleContestMatchInfo {
  time_text: string
  match_name: string
  is_progress: boolean
}

/** home_status=2 时当前赛程，对应 match_info 中 is_progress 项下标 */
export enum MiddleContestHomeMatchStage {
  /** 海选赛 */
  audition = 0,
  /** 分组晋级 */
  groupAdvancement = 1,
  /** 全民助力 */
  nationalAssist = 2,
}

/** homepage.role 用户身份 */
export enum MiddleContestUserRole {
  /** 普通人（无公会，仅闯关任务区展示加入公会引导） */
  normal = 0,
  /** 主播 */
  anchor = 1,
  /** 公会长 */
  guildLeader = 2,
}

export const isMiddleContestGuildMember = (
  role: MiddleContestUserRole | number
): boolean =>
  role === MiddleContestUserRole.anchor ||
  role === MiddleContestUserRole.guildLeader

/** 奖励 Item（切图 / 预热 / 榜单等通用结构） */
export interface MiddleContestRewardItem {
  id: number
  view_icon: string
  toast_name: string
  reward_type: number
  tag: string
  privilege_id?: number | null
  reward_config_id?: number | null
  quantity: number
  reward_value: number
  has_show_effect: boolean
}

/** 活动礼物（全赛程状态通用） */
export interface MiddleContestEventGifts {
  /** 点击去送礼的礼物 id */
  gift_id: number
  gift_items: MiddleContestRewardItem[]
}

/** 预热页文案插值数据 */
export interface MiddleContestPreheatData {
  /** 入围名次；全球赛.预热 Top{0}、头图.标语 Top{1} */
  finalist_ranking: number
  /** 入围奖励价值；头图.标语 ${0} */
  finalist_reward_value: number
  /** 分组赛最高金币；分组赛奖励.预热 {0} */
  group_rank_max_coin: number
  /** 分组赛最高倍率；分组赛奖励.预热 {1} */
  group_rank_max_card_rate: number
  /** 点赞转积分；全民助力.预热 {0} */
  like_to_score: number
}

/** 进行中 · 奖励说明 Tab 区块累计奖励文案插值数据 */
export interface MiddleContestRewardData {
  /** 分组赛.累计奖励 {0}：累计金币 */
  preliminary_total_coin: number
  /** 分组赛.累计奖励 {1}：获奖公会数（文案展示为 {1}+） */
  preliminary_winning_union_count: number
  /** 地区决赛.累计奖励 {0}：累计金币 */
  regional_final_total_coin: number
  /** 地区决赛.累计奖励 {1}：获奖公会数（文案展示为 {1}+） */
  regional_final_winning_union_count: number
  /** 全球巅峰赛.累计奖励 {0}：累计金币 */
  global_total_coin: number
}

export interface MiddleContestHomepageTabItem {
  /** 分组 tab 编号；1-4 对应铂金/钻石/黄金/新星 */
  tab_id?: number
  /** 分组 tab 展示名（多语言由后端下发） */
  tab_name?: string
  /** 是否展示「新」角标 */
  is_new?: boolean
  /** 当前所在 tab 编号；1-4 */
  is_current_tab?: number
}

/** POST /live/activity/get_2026_year_middle_contest_homepage */
export interface MiddleContestHomepageResponse {
  err_msg?: string
  home_status?: MiddleContestHomeStatus | number
  match_info?: MiddleContestMatchInfo[]
  /** 赛程倒计时（秒） */
  countdown?: number
  /** 充值福利 icon 倒计时（秒） */
  recharge_countdown?: number
  /** 充值福利跳转链接（客户端 scheme） */
  recharge_url?: string
  /** 年中聚合页跳转链接（客户端 scheme） */
  year_middle_home_url?: string
  event_gifts?: MiddleContestEventGifts
  lottery_id?: number
  /** 海选直通车当前榜日期 YYYYMMDD，须在 audtion_to_next_date_list 内 */
  audtion_to_next_cur_date?: string
  audtion_to_next_date_list?: string[]
  preheat_data?: MiddleContestPreheatData
  /** 进行中 · 奖励说明 Tab 三阶段累计奖励文案插值 */
  reward_data?: MiddleContestRewardData
  /** 0 普通人 / 1 主播 / 2 公会长 */
  role?: MiddleContestUserRole | number
  /** 用户所在房间 id；去送礼 openRoom 使用 */
  room_id?: number
  /** 分组晋级赛 tab 展示配置 */
  tab_list?: MiddleContestHomepageTabItem[]
}

export interface MiddleContestHomepageRequestParams {
  /** 来源，礼物面板传 banner */
  from?: string
  /** 切换赛程查看：0 海选 / 1 地区决赛 / 2 全球巅峰赛 */
  match?: MiddleContestHomeMatchStage | number
}

export interface MiddleContestHomepageState {
  err_msg: string
  home_status: MiddleContestHomeStatus
  match_info: MiddleContestMatchInfo[]
  /** home_status=2 时当前赛程；home_status=3 时为最后一程全民助力（2）；其余为 null */
  current_match?: MiddleContestHomeMatchStage | null
  countdown: number
  recharge_countdown: number
  recharge_url: string
  year_middle_home_url: string
  event_gifts: MiddleContestEventGifts
  lottery_id: number
  audtion_to_next_cur_date: string
  audtion_to_next_date_list: string[]
  preheat_data: MiddleContestPreheatData
  reward_data: MiddleContestRewardData
  role: MiddleContestUserRole
  /** 用户所在房间 id；去送礼 openRoom 使用 */
  room_id: number
  /** 分组晋级赛 tab 展示配置 */
  tab_list: MiddleContestHomepageTabItem[]
}

export interface MiddleContestHomepageStoreState {
  loading: boolean
  error: string | null
  data: MiddleContestHomepageState
}

/** POST /live/activity/get_2026_year_middle_contest_reward_list */
export interface MiddleContestRewardListRequestParams {
  from?: string
}

export interface MiddleContestRewardListRankReward {
  rank_label: string
  reward: MiddleContestRewardItem
}

/** 海选直通车奖励 */
export interface MiddleContestRewardListToNextRankReward {
  rank_label?: string
  reward: MiddleContestRewardItem
}

export interface MiddleContestRewardListExpectedReward {
  variant: 'gift' | 'booster'
  reward: MiddleContestRewardItem
}

/** from=2 单行奖励；layout 由前端按 rows 下标写死，接口不下发 */
export interface MiddleContestRewardListGlobalRow {
  items: MiddleContestRewardItem[]
}

/** from=1 预热 · 地区赛 */
export interface MiddleContestRewardListFrom1Data {
  err_msg?: string
  activity_id?: number
  preliminary?: {
    rank_rewards?: MiddleContestRewardListRankReward[]
    /** 海选直通车奖励，按实际条数展示 */
    to_next_rank_rewards?: MiddleContestRewardListToNextRankReward[]
    expected_rewards?: MiddleContestRewardListExpectedReward[]
  }
  group_advancement?: {
    reward_rows?: MiddleContestRewardItem[][][]
  }
  challenge_mission?: {
    reward_rows?: MiddleContestRewardItem[][]
  }
}

/** from=2 预热 · 全球赛 */
export interface MiddleContestRewardListFrom2Data {
  err_msg?: string
  activity_id?: number
  rows?: MiddleContestRewardListGlobalRow[]
}

/** from=3 进行中 · 奖励说明（三阶段完整数据，一次返回） */
export interface MiddleContestRewardListStageRankGroup {
  rank_label: string
  /** preliminary / group 使用平铺奖励列表 */
  items?: MiddleContestRewardItem[]
  /** global 使用多行混合布局（礼物行 + 紧凑金币行） */
  rows?: MiddleContestRewardItem[][]
}

/**
 * from=3 奖励说明区块
 * theme 区分三大赛程段，前端据此选择 UI 主题与布局：
 * - preliminary：海选 · gift-card
 * - group：地区决赛 · compact-row（tab_rank_groups 四组 Tab）
 * - global：全球巅峰赛 · mixed（rows 多行）
 */
export interface MiddleContestRewardListStageSection {
  theme: 'preliminary' | 'group' | 'global'
  /** theme=preliminary / global 时使用 */
  rank_groups?: MiddleContestRewardListStageRankGroup[]
  /** theme=group 时使用；长度 3 时无黄金组，长度 4 时为铂金 / 钻石 / 黄金 / 新星 */
  tab_rank_groups?: MiddleContestRewardListStageRankGroup[][]
}

/** from=3 进行中 · 奖励说明 Tab（sections 含三阶段完整数据） */
export interface MiddleContestRewardListFrom3Data {
  err_msg?: string
  activity_id?: number
  sections?: MiddleContestRewardListStageSection[]
}

export type MiddleContestRewardListResponse =
  | MiddleContestRewardListFrom1Data
  | MiddleContestRewardListFrom2Data
  | MiddleContestRewardListFrom3Data
  | (Record<string, unknown> & {err_msg?: string})

export interface MiddleContestRewardListState {
  from: string
  err_msg: string
  /** 除 err_msg 外的原始字段，便于后续补类型时接入 */
  raw: Record<string, unknown>
}

export interface MiddleContestRewardListStoreState {
  loading: boolean
  error: string | null
  data: MiddleContestRewardListState
}

export interface NewYearUnionGame2026State {
  homepage: MiddleContestHomepageStoreState
  action: MiddleContestUnionActionState
  remindUseCard: MiddleContestRemindUseCardState
  lottery: MiddleContestLotteryState
  auditionToRank: MiddleContestAuditionToRankStoreState
  rank: MiddleContestRankStoreState
  rewardList: MiddleContestRewardListStoreState
  card: MiddleContestCardStoreState
  person: MiddleContestPersonListStoreState
  repechage: MiddleContestRepechageStoreState
  taskRecord: MiddleContestTaskRecordStoreState
  task: MiddleContestTaskState
}
